import {APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, mergeApplicationConfig, provideZoneChangeDetection} from '@angular/core';
import {catchError, firstValueFrom, of} from 'rxjs';
import {FeatureService} from './common/services/feature.service';
import {ProjectFeatureService} from './services/project-feature.service';
import {AuthService} from './centrui/src/app/services/auth.service';
import {BrowserModule} from "@angular/platform-browser";
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr} from "@angular/common/http";
import {provideServiceWorker} from "@angular/service-worker";
import {environment} from "../environments/environment";
import {AppRoutingModule} from "./app-routing.module";
import {provideAnimations} from "@angular/platform-browser/animations";
import {ErrorInterceptor} from "./centrui/src/app/interceptors/error.interceptor";
import {JwtInterceptor} from "./centrui/src/app/interceptors/jwt.interceptor";
import { DragulaModule } from 'ng2-dragula';
import { LucideAngularModule } from 'lucide-angular';
import * as Sentry from '@sentry/angular';
import {centruiAppConfig} from "./centrui/centrui.config";
import { PRESENTATION_BUILDER_LUCIDE_ICONS } from './lumi-slides/src/app/features/presentation-builder/presentation-builder-lucide-icons';
import { PRESENTATION_IMAGE_RESOLVER } from './lumi-slides/src/app/features/presentation-builder/services/presentation-image-resolver';
import { PRESENTATION_SAVE_SERVICE } from './lumi-slides/src/app/features/presentation-builder/services/presentation-save.service';
import { PolarixPresentationImageResolverService } from './common/services/polarix-presentation-image-resolver.service';
import { PolarixPresentationSaveService } from './common/services/polarix-presentation-save.service';
import { LEXICON_MODAL_COMPONENT } from './common/constants/lexicon-modal.token';

export function scopes(): string[] {
  return [
    'user.read',
    'openid',
    'profile',
    'People.Read',
    'User.Read',
    'email',
    'People.Read.All',
    'offline_access'
  ];
}

/** True if the current path is a project route that has a slug (e.g. /projects/view/my-slug). */
function isProjectSlugRoute(): boolean {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  return /^\/projects\/(?!create|template)[^/]+\/[^/]+/.test(pathname);
}

/** Extracts the project slug from the current path (e.g. /projects/view/my-slug -> my-slug). */
function getProjectSlugFromPath(): string | null {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const match = pathname.match(/^\/projects\/(?!create|template)[^/]+\/([^/]+)/);
  return match ? match[1] : null;
}

export function loadFeaturesInitializer(featureService: FeatureService, authService: AuthService): () => Promise<void> {
  return () => {
    if (!authService.userObject) return Promise.resolve();
    return firstValueFrom(featureService.refresh(authService.userObject.customer_id).pipe(catchError(() => of(null)))).then(features => {
      if (features) {
        featureService.markInitialLoadDone();
      }
      return undefined;
    });
  };
}

export function loadProjectFeaturesInitializer(
  projectFeatureService: ProjectFeatureService,
  authService: AuthService
): () => Promise<void> {
  return () => {
    if (!authService.userObject || !isProjectSlugRoute()) return Promise.resolve();
    const projectSlug = getProjectSlugFromPath();
    if (!projectSlug) return Promise.resolve();
    return firstValueFrom(projectFeatureService.refresh(projectSlug).pipe(catchError(() => of(null)))).then(() => undefined);
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    importProvidersFrom(
      BrowserModule,
      AppRoutingModule,
      DragulaModule.forRoot(),
      LucideAngularModule.pick(PRESENTATION_BUILDER_LUCIDE_ICONS),
    ),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideAnimations(),
    provideServiceWorker('ngsw-worker.js', { enabled: environment.serviceWorkerEnabled, registrationStrategy: 'registerWhenStable:30000' }),
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    {
      provide: APP_INITIALIZER,
      useFactory: loadFeaturesInitializer,
      deps: [FeatureService, AuthService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: loadProjectFeaturesInitializer,
      deps: [ProjectFeatureService, AuthService],
      multi: true
    },
    {
      provide: PRESENTATION_IMAGE_RESOLVER,
      useExisting: PolarixPresentationImageResolverService,
    },
    {
      provide: PRESENTATION_SAVE_SERVICE,
      useExisting: PolarixPresentationSaveService,
    },
    {
      provide: LEXICON_MODAL_COMPONENT,
      useValue: () => import('./components/modals/lexicon/lexicon.component').then(m => m.LexiconComponent),
    },
  ]
};

export const rootAppConfig = mergeApplicationConfig(appConfig, centruiAppConfig);
